import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, Search, X } from 'lucide-react';
import { api } from '../utils/client';

export interface HistoryOrder {
  id: number;
  project: number | null;
  project_name: string | null;
  product_name: string;
  product_service_type?: string | null;
  display_status: 'Draft' | 'Active' | 'Completed' | 'Cancelled' | string;
  created_at: string;
  completed_at: string | null;
  credits: number;
  placed_by: string;
  go_filename: string | null;
  go_name: string | null;
  order_number?: string | null;
}

interface OrderHistoryProps {
  onBack: () => void;
  onContactStrategist: () => void;
}

type SortKey = 'newest' | 'oldest';

type HistoryFilters = {
  projects: string[];
  products: string[];
  statuses: string[];
  placedBy: string[];
  dateFrom: string;
  dateTo: string;
};

const EMPTY_FILTERS: HistoryFilters = {
  projects: [],
  products: [],
  statuses: [],
  placedBy: [],
  dateFrom: '',
  dateTo: '',
};

const STATUS_OPTIONS = ['Draft', 'Active', 'Completed', 'Cancelled'];
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];
const FILTER_PREVIEW_COUNT = 5;

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_DOT: Record<string, string> = {
  Completed: 'bg-[#2E9B4F]',
  Active: 'bg-[#E6B325]',
  Cancelled: 'bg-[#D64545]',
  Draft: 'bg-[#9A9A9A]',
};

function formatOrderDate(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function serviceTypeLabel(serviceType?: string | null): string {
  if (serviceType === 'AI') return 'Catalyx AI';
  return serviceType || '-';
}

function serviceTypePillClass(serviceType?: string | null): string {
  if (serviceType === 'AI') return 'bg-[#E4F3C8] text-[#3F6B1A]';
  if (serviceType === 'Catalyx') return 'bg-[#EDE7F6] text-[#674fa3]';
  if (serviceType === 'AI Off-Platform') return 'bg-[#E3F2FD] text-[#1565C0]';
  return 'bg-[#F3F3F3] text-[#4A4A4A]';
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(values.map((value) => (value || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
}

function toggleListValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function parseUkDate(value: string, atEndOfDay = false): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(
    year,
    month - 1,
    day,
    atEndOfDay ? 23 : 0,
    atEndOfDay ? 59 : 0,
    atEndOfDay ? 59 : 0,
    atEndOfDay ? 999 : 0
  );
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date.getTime();
}

function hasActiveFilters(filters: HistoryFilters): boolean {
  return (
    filters.projects.length > 0 ||
    filters.products.length > 0 ||
    filters.statuses.length > 0 ||
    filters.placedBy.length > 0 ||
    Boolean(filters.dateFrom) ||
    Boolean(filters.dateTo)
  );
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ onBack, onContactStrategist }) => {
  const [orders, setOrders] = useState<HistoryOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<HistoryFilters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<HistoryFilters>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [previewOrder, setPreviewOrder] = useState<HistoryOrder | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showAllOptions, setShowAllOptions] = useState<Record<string, boolean>>({});
  const filterRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api<HistoryOrder[] | { results: HistoryOrder[] }>('/api/orders/')
      .then((data) => {
        if (cancelled) return;
        setOrders(Array.isArray(data) ? data : data?.results ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load orders');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projectOptions = useMemo(
    () => uniqueSorted(orders.map((order) => order.project_name)),
    [orders]
  );
  const productOptions = useMemo(
    () => uniqueSorted(orders.map((order) => order.product_name)),
    [orders]
  );
  const placedByOptions = useMemo(
    () => uniqueSorted(orders.map((order) => order.placed_by)),
    [orders]
  );

  useEffect(() => {
    if (!filterOpen && !sortOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterOpen && !filterRef.current?.contains(target)) setFilterOpen(false);
      if (sortOpen && !sortRef.current?.contains(target)) setSortOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFilterOpen(false);
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filterOpen, sortOpen]);

  const filteredOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const fromTime = parseUkDate(appliedFilters.dateFrom);
    const toTime = parseUkDate(appliedFilters.dateTo, true);
    const next = orders.filter((order) => {
      if (
        appliedFilters.projects.length > 0 &&
        !appliedFilters.projects.includes((order.project_name || '').trim())
      ) {
        return false;
      }
      if (
        appliedFilters.products.length > 0 &&
        !appliedFilters.products.includes((order.product_name || '').trim())
      ) {
        return false;
      }
      if (
        appliedFilters.statuses.length > 0 &&
        !appliedFilters.statuses.includes(order.display_status)
      ) {
        return false;
      }
      if (
        appliedFilters.placedBy.length > 0 &&
        !appliedFilters.placedBy.includes((order.placed_by || '').trim())
      ) {
        return false;
      }
      const created = new Date(order.created_at).getTime();
      if (fromTime != null && created < fromTime) return false;
      if (toTime != null && created > toTime) return false;
      if (!query) return true;
      const haystack = [
        order.project_name,
        order.product_name,
        String(order.id),
        order.order_number,
        order.placed_by,
        order.display_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });

    next.sort((a, b) => {
      const delta =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortKey === 'oldest' ? delta : -delta;
    });

    return next;
  }, [orders, appliedFilters, searchQuery, sortKey]);

  const pageCount = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedOrders = filteredOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, searchQuery, sortKey, pageSize]);

  const toggleExpanded = (id: number) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const openFilter = () => {
    setDraftFilters(appliedFilters);
    setSortOpen(false);
    setFilterOpen((open) => !open);
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setFilterOpen(false);
  };

  const clearDraftFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
  };

  const toggleSection = (key: string) => {
    setCollapsedSections((current) => ({ ...current, [key]: !current[key] }));
  };

  const renderCheckboxList = (
    sectionKey: string,
    options: string[],
    selected: string[],
    onToggle: (value: string) => void
  ) => {
    const expanded = Boolean(showAllOptions[sectionKey]);
    const visible = expanded ? options : options.slice(0, FILTER_PREVIEW_COUNT);
    const remaining = options.length - visible.length;
    return (
      <div className="mt-2">
        {visible.map((option) => (
          <label key={option} className="flex items-center gap-2 py-[5px] cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => onToggle(option)}
              className="h-4 w-4 shrink-0 rounded-[3px] border border-[#C8C8C8] accent-[#3da3d4]"
            />
            <span className="text-[13px] text-[#4A4A4A] leading-none">{option}</span>
          </label>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() =>
              setShowAllOptions((current) => ({ ...current, [sectionKey]: true }))
            }
            className="mt-1 text-[13px] text-[#3da3d4] hover:text-[#2d8ab8]"
          >
            + {remaining} more
          </button>
        )}
      </div>
    );
  };

  const renderFilterSection = (
    key: string,
    title: string,
    children: React.ReactNode
  ) => {
    const collapsed = Boolean(collapsedSections[key]);
    return (
      <div className="px-3 py-3 border-b border-[#E8E8E8]">
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className="flex w-full items-center justify-between gap-2"
        >
          <span className="text-[13px] font-bold text-[#0C0B0B]">{title}</span>
          <ChevronDown
            className={`w-4 h-4 text-[#6B6B6B] transition-transform ${
              collapsed ? '-rotate-90' : ''
            }`}
          />
        </button>
        {!collapsed && children}
      </div>
    );
  };

  return (
    <div className="w-full min-h-full bg-white font-[Inter,sans-serif] px-8 pt-8 pb-12">
      <div className="max-w-[1128px]">
        <h1 className="text-[32px] leading-none font-bold text-[#0C0B0B]">Order History</h1>
        <p className="mt-3 text-[14px] text-[#626262] max-w-[720px]">
          Track the progress of your purchases, access completed deliverables, download outputs, and
          revisit previous innovation projects.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 inline-flex items-center gap-1 text-[14px] text-[#3ea2d3] hover:text-[#2d8ab8] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="underline">Back to Marketplace</span>
        </button>
        <p className="mt-4 text-[14px] text-[#626262]">
          Need help with any of your orders?{' '}
          <button
            type="button"
            onClick={onContactStrategist}
            className="underline text-[#3da3d4] hover:text-[#2d8ab8] transition-colors"
          >
            Contact a Catalyx Strategist
          </button>
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div ref={filterRef} className="relative">
            <button
              type="button"
              onClick={openFilter}
              className={`flex h-10 min-w-[120px] items-center justify-between gap-2 rounded-md border bg-white px-3 text-sm ${
                filterOpen
                  ? 'border-[#0C0B0B] text-[#0C0B0B] font-medium'
                  : hasActiveFilters(appliedFilters)
                    ? 'border-[#0C0B0B] text-[#0C0B0B]'
                    : 'border-[#D1D1D1] text-[#4A4A4A]'
              }`}
            >
              Filter
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {filterOpen && (
              <div className="absolute left-0 top-full z-30 mt-1 w-[280px] overflow-hidden rounded-lg border border-[#E8E8E8] bg-white shadow-lg">
                <div className="max-h-[420px] overflow-y-auto">
                  {renderFilterSection(
                    'project',
                    'Project',
                    renderCheckboxList(
                      'project',
                      projectOptions,
                      draftFilters.projects,
                      (value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          projects: toggleListValue(current.projects, value),
                        }))
                    )
                  )}
                  {renderFilterSection(
                    'product',
                    'Product',
                    renderCheckboxList(
                      'product',
                      productOptions,
                      draftFilters.products,
                      (value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          products: toggleListValue(current.products, value),
                        }))
                    )
                  )}
                  {renderFilterSection(
                    'status',
                    'Status',
                    renderCheckboxList(
                      'status',
                      STATUS_OPTIONS,
                      draftFilters.statuses,
                      (value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          statuses: toggleListValue(current.statuses, value),
                        }))
                    )
                  )}
                  {renderFilterSection(
                    'date',
                    'Date range',
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="dd/mm/yyyy"
                        value={draftFilters.dateFrom}
                        onChange={(event) =>
                          setDraftFilters((current) => ({
                            ...current,
                            dateFrom: event.target.value,
                          }))
                        }
                        className="h-9 w-full min-w-0 rounded-md border border-[#E0E0E0] px-2 text-[12px] text-[#4A4A4A] placeholder:text-[#B3B3B3] focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
                      />
                      <span className="text-[#9A9A9A]">–</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="dd/mm/yyyy"
                        value={draftFilters.dateTo}
                        onChange={(event) =>
                          setDraftFilters((current) => ({
                            ...current,
                            dateTo: event.target.value,
                          }))
                        }
                        className="h-9 w-full min-w-0 rounded-md border border-[#E0E0E0] px-2 text-[12px] text-[#4A4A4A] placeholder:text-[#B3B3B3] focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
                      />
                    </div>
                  )}
                  {renderFilterSection(
                    'placedBy',
                    'Placed by',
                    renderCheckboxList(
                      'placedBy',
                      placedByOptions,
                      draftFilters.placedBy,
                      (value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          placedBy: toggleListValue(current.placedBy, value),
                        }))
                    )
                  )}
                </div>
                <div className="flex items-center justify-between border-t border-[#E8E8E8] px-3 py-3">
                  <button
                    type="button"
                    onClick={clearDraftFilters}
                    className="text-[13px] text-[#6B6B6B] hover:text-[#0C0B0B]"
                  >
                    Clear all
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="h-8 rounded-md bg-[#3da3d4] px-4 text-[13px] font-medium text-white hover:bg-[#2d8ab8]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <div ref={sortRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setFilterOpen(false);
                setSortOpen((open) => !open);
              }}
              className={`flex h-10 min-w-[120px] items-center justify-between gap-2 rounded-md border bg-white px-3 text-sm ${
                sortOpen ? 'border-[#0C0B0B] text-[#0C0B0B] font-medium' : 'border-[#D1D1D1] text-[#4A4A4A]'
              }`}
            >
              Sort
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${sortOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {sortOpen && (
              <ul className="absolute left-0 top-full z-30 mt-1 min-w-full overflow-hidden rounded-lg border border-[#E8E8E8] bg-white py-1 shadow-lg">
                {SORT_OPTIONS.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setSortKey(option.value);
                        setSortOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        sortKey === option.value
                          ? 'font-medium text-[#3da3d4]'
                          : 'text-[#4A4A4A]'
                      }`}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="relative flex-1 min-w-[220px] max-w-[360px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search orders"
              className="h-10 w-full rounded-md border border-[#D1D1D1] bg-white pl-9 pr-3 text-sm text-[#0C0B0B] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-1 focus:ring-[#674fa3]"
            />
          </div>
          <label className="ml-auto flex items-center gap-2 text-[13px] text-[#4A4A4A]">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="h-10 rounded-md border border-[#D1D1D1] bg-white px-2 text-sm text-[#0C0B0B] focus:outline-none focus:ring-1 focus:ring-[#674fa3]"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[760px] border-separate border-spacing-0">
            <thead>
              <tr className="text-left text-[13px] font-semibold text-[#0C0B0B]">
                <th className="py-3 pr-4">Project name</th>
                <th className="py-3 pr-4">Product</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Date completed</th>
                <th className="py-3 pr-4">Results</th>
                <th className="py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="py-10 text-sm text-[#8A8A8A]">
                    Loading orders…
                  </td>
                </tr>
              )}
              {error && !isLoading && (
                <tr>
                  <td colSpan={6} className="py-10 text-sm text-red-600">
                    {error}
                  </td>
                </tr>
              )}
              {!isLoading && !error && pagedOrders.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-sm text-[#8A8A8A]">
                    No orders match your filters.
                  </td>
                </tr>
              )}
              {pagedOrders.map((order) => {
                const expanded = expandedId === order.id;
                const canOpenGo = Boolean(order.project && order.go_filename);
                return (
                  <React.Fragment key={order.id}>
                    <tr className="text-[13px] text-[#0C0B0B] border-t border-[#E8E8E8]">
                      <td className="py-4 pr-4 border-t border-[#E8E8E8]">
                        {order.project_name || '-'}
                      </td>
                      <td className="py-4 pr-4 border-t border-[#E8E8E8]">{order.product_name}</td>
                      <td className="py-4 pr-4 border-t border-[#E8E8E8]">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              STATUS_DOT[order.display_status] || 'bg-[#9A9A9A]'
                            }`}
                          />
                          {order.display_status}
                        </span>
                      </td>
                      <td className="py-4 pr-4 border-t border-[#E8E8E8]">
                        {formatOrderDate(order.completed_at)}
                      </td>
                      <td className="py-4 pr-4 border-t border-[#E8E8E8]">
                        {canOpenGo ? (
                          <span className="inline-flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => setPreviewOrder(order)}
                              className="text-[#3da3d4] underline hover:text-[#2d8ab8]"
                            >
                              Preview
                            </button>
                            <a
                              href={`/api/projects/${order.project}/reports/${encodeURIComponent(
                                order.go_filename || ''
                              )}/download/`}
                              className="text-[#3da3d4] underline hover:text-[#2d8ab8]"
                            >
                              Download
                            </a>
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-4 border-t border-[#E8E8E8]">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(order.id)}
                          className="p-1 text-[#4A4A4A] hover:text-[#0C0B0B]"
                          aria-label={expanded ? 'Collapse order details' : 'Expand order details'}
                        >
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={6} className="bg-[#FAFAFA] px-4 py-5 border-b border-[#E8E8E8]">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-4 text-[13px]">
                            <div>
                              <p className="text-[#8A8A8A]">Order no.</p>
                              <p className="mt-1 text-[#0C0B0B]">
                                {order.order_number?.trim() ? order.order_number : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#8A8A8A]">Purchase method</p>
                              <p className="mt-1">
                                <span
                                  className={`inline-flex px-2.5 py-0.5 rounded-full text-[12px] font-medium ${serviceTypePillClass(
                                    order.product_service_type
                                  )}`}
                                >
                                  {serviceTypeLabel(order.product_service_type)}
                                </span>
                              </p>
                            </div>
                            <div />
                            <div>
                              <p className="text-[#8A8A8A]">Request date</p>
                              <p className="mt-1 text-[#0C0B0B]">{formatOrderDate(order.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-[#8A8A8A]">Credit amount</p>
                              <p className="mt-1 text-[#0C0B0B]">{order.credits} credits</p>
                            </div>
                            <div />
                            <div>
                              <p className="text-[#8A8A8A]">Purchase date</p>
                              <p className="mt-1 text-[#0C0B0B]">
                                {order.display_status === 'Draft'
                                  ? '-'
                                  : formatOrderDate(order.created_at)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#8A8A8A]">Placed by</p>
                              <p className="mt-1 text-[#0C0B0B]">{order.placed_by || '-'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div className="mt-4 flex justify-end gap-2 text-sm text-[#4A4A4A]">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="px-3 py-1 rounded-md border border-[#D1D1D1] disabled:opacity-40"
            >
              Previous
            </button>
            <span className="px-2 py-1">
              {currentPage} / {pageCount}
            </span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              className="px-3 py-1 rounded-md border border-[#D1D1D1] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {previewOrder?.project && previewOrder.go_filename && (
        <div
          className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-go-preview-title"
          onClick={() => setPreviewOrder(null)}
        >
          <div
            className="w-full max-w-5xl h-[80vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E8E8]">
              <h2 id="order-go-preview-title" className="text-lg font-bold text-[#0C0B0B]">
                {previewOrder.go_name || previewOrder.product_name}
              </h2>
              <button
                type="button"
                onClick={() => setPreviewOrder(null)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label="Close preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              title={previewOrder.go_name || 'Guaranteed output preview'}
              src={`/api/projects/${previewOrder.project}/reports/${encodeURIComponent(
                previewOrder.go_filename
              )}/preview/`}
              className="flex-1 w-full border-0 bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
